import { defineStore } from "pinia";
import { AgencyClient } from "@/api";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { VITE_APP_ATMO_SERVICE_ID } from "@/env";
import { useProjectsStore } from "@/store/modules/projects";
import { logRequestFailure } from "@/utils/network";

const { innerFetch } = crudGenerator(db.models.Agency);

export const useAgenciesStore = defineStore("agencies", {
  state: () => ({
    currentProjectAgency: null,
    agenciesIsLoading: false,
  }),
  actions: {
    async setCurrentProjectAgency(agencyCode) {
      this.agenciesIsLoading = true;
      try {
        this.currentProjectAgency = await this.fetchAgencyByCode(agencyCode);
      } catch (error) {
        logRequestFailure("Could not fetch the project agency", error);
        this.currentProjectAgency = null;
      } finally {
        this.agenciesIsLoading = false;
      }

      return this.currentProjectAgency;
    },

    async fetchAgencies(metadata = {}) {
      return innerFetch(
        { ...metadata, applicationName: VITE_APP_ATMO_SERVICE_ID },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.agenciesIsLoading = isLoading;
        }
      );
    },

    async fetchAgencyByUuid(agencyUuid = "") {
      const dexieAgency = await db.agencies
        .where("uuid")
        .equals(agencyUuid)
        .first();

      if (dexieAgency?.uuid) {
        return dexieAgency;
      }

      const agency = await AgencyClient.retrieve({ uuid: agencyUuid });

      await db.models.Agency.createOrUpdate(agency);
      return agency;
    },

    async fetchAgencyByCode(agencyCode = "") {
      if (!agencyCode) {
        return null;
      }
      const dexieAgency = await db.agencies
        .where("code")
        .equals(agencyCode)
        .first();

      if (dexieAgency?.uuid) {
        return dexieAgency;
      }

      const agency = await AgencyClient.retrieveByCode({ code: agencyCode });
      await db.models.Agency.createOrUpdate(agency);
      return agency;
    },

    async fetchAgencyByUserUuid(userUuid) {
      return AgencyClient.retrieveByUserUuid({ uuid: userUuid });
    },
  },
});
